import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./PendingRequestsPopdown.css";
import ReactPaginate from "react-paginate";
import { approveOnboarding, denyOnboarding } from "./dealerOnboardingApi";

const PendingRequestsPopdown = ({ show, onClose, requests = [], onUpdateRequests }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [actionType, setActionType] = useState("");
  const requestsPerPage = 6;

  // Show both pending and denied requests
  const visibleRequests = requests.filter(
    (request) => request.status === "pending" || request.status === "denied"
  );

  const offset = currentPage * requestsPerPage;
  const currentRequests = visibleRequests.slice(offset, offset + requestsPerPage);
  const pageCount = Math.ceil(visibleRequests.length / requestsPerPage);

  const trulySelectedCount = Object.values(selectedRequests).filter(Boolean).length;

  useEffect(() => {
    const allCurrentSelected =
      currentRequests.length > 0 &&
      currentRequests.every((request) => selectedRequests[request.id]);
    setSelectedAll(allCurrentSelected);
  }, [selectedRequests, currentRequests]);

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
    setSelectedAll(false);
  };

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setSelectedAll(isChecked);
    const newSelectedRequests = { ...selectedRequests };
    currentRequests.forEach((request) => {
      newSelectedRequests[request.id] = isChecked;
    });
    setSelectedRequests(newSelectedRequests);
  };

  const handleCheckboxChange = (event, requestId) => {
    event.stopPropagation();
    const isChecked = event.target.checked;
    setSelectedRequests((prevState) => ({
      ...prevState,
      [requestId]: isChecked,
    }));
  };

  const handleRowClick = (requestId) => {
    setSelectedRequests((prevState) => ({
      ...prevState,
      [requestId]: !prevState[requestId],
    }));
  };

  const handleBulkAction = (type) => {
    const selectedIds = Object.keys(selectedRequests).filter((id) => selectedRequests[id]);
    if (selectedIds.length > 0) {
      setActionType(type);
      setShowConfirmation(true);
    } else {
      alert(t("pleaseSelectAtLeastOneRequest"));
    }
  };

  const handleConfirm = async () => {
    const selectedDealers = requests.filter((r) => selectedRequests[r.id]);
    if (selectedDealers.length === 0) {
      setShowConfirmation(false);
      return;
    }

    setLoading(true);
    try {
      const payload = selectedDealers.map((dealer) => ({
        parentGroup: dealer.parentGroup,
        dealerMapCode: dealer.dealerMapCode,
        locationCode: dealer.locationCode,
      }));

      let response;
      if (actionType === "approve") {
        response = await approveOnboarding(payload);
        // Update parent state to remove approved requests
        onUpdateRequests((prev) => prev.filter((req) => !selectedRequests[req.id]));
      } else if (actionType === "deny") {
        response = await denyOnboarding(payload);
        // Update parent state to mark denied requests
        onUpdateRequests((prev) =>
          prev.map((req) =>
            selectedRequests[req.id] ? { ...req, status: "denied" } : req
          )
        );
      }

      if (response) {
        setMessages((prev) => [...prev, ...response]);
      }
    } catch (error) {
      alert("Failed to update dealer status!");
    } finally {
      setLoading(false);
      setShowConfirmation(false);
      setSelectedRequests({});
      setSelectedAll(false);

      if (currentPage > 0 && visibleRequests.length <= requestsPerPage) {
        setCurrentPage(0);
      }
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setActionType("");
  };

  if (!show) return null;

  return (
    <div className="modal-backdrop-custom">
      <div className={`modal-popup ${visibleRequests.length === 0 ? "modal-popup-empty" : ""}`}>
        <div className="modal-contents">
          <div className="modal-header">
            <h5 className="modal-title">
              {t("pendingOnboardingRequests")}{" "}
              <span style={{ color: "#007BFF" }}>({visibleRequests.length})</span>
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {visibleRequests.length > 0 ? (
              <>
                <table className="table table-borderless">
                  <thead>
                    <tr>
                      <th>
                        <input type="checkbox" checked={selectedAll} onChange={handleSelectAll} />
                      </th>
                      <th>{t("massName")}</th>
                      <th>{t("massCode")}</th>
                      <th>{t("region")}</th>
                      <th>{t("onboardingDate")}</th>
                      <th>{t("status")}</th>
                      {trulySelectedCount <= 1 && <th colSpan={2}>{t("actions")}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {currentRequests.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => handleRowClick(r.id)}
                        className={`clickable-row ${selectedRequests[r.id] ? "selected" : ""}`}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedRequests[r.id] || false}
                            onChange={(event) => handleCheckboxChange(event, r.id)}
                          />
                        </td>
                        <td>{r.name}</td>
                        <td>{r.code}</td>
                        <td>{r.region}</td>
                        <td>{r.date ? r.date.split('T')[0] : r.date}</td>
                        <td>{r.status === "denied" ? t("denied") : t("pending")}</td>
                        {trulySelectedCount <= 1 && (
<td className="actions-cell">
  <button
    className="btn btn-success btn-sm action-button"
    style={{ marginRight: "10px" }}
    onClick={(e) => {
      e.stopPropagation();
      // Directly set actionType and show confirmation for this row
      setSelectedRequests({ [r.id]: true });
      setActionType("approve");
      setShowConfirmation(true);
    }}
  >
    {t("approve")}
  </button>
  <button
    className="btn btn-danger btn-sm action-button"
    onClick={(e) => {
      e.stopPropagation();
      setSelectedRequests({ [r.id]: true });
      setActionType("deny");
      setShowConfirmation(true);
    }}
  >
    {t("deny")}
  </button>
</td>

                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {trulySelectedCount > 1 && (
                  <div className="bulk-actions">
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleBulkAction("approve")}
                      style={{ marginRight: "10px", height: "48px" }}
                    >
                      {t("approveAll")}
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleBulkAction("deny")}
                      style={{ height: "48px" }}
                    >
                      {t("denyAll")}
                    </button>
                  </div>
                )}

                {visibleRequests.length > requestsPerPage && (
                  <ReactPaginate
                    previousLabel={"<"}
                    nextLabel={">"}
                    breakLabel={"..."}
                    pageCount={pageCount}
                    marginPagesDisplayed={1}
                    pageRangeDisplayed={1}
                    onPageChange={handlePageClick}
                    containerClassName={"pagination"}
                    activeClassName={"active"}
                    pageClassName={"page-item"}
                    pageLinkClassName={"page-link"}
                    previousClassName={"page-item"}
                    previousLinkClassName={"page-link"}
                    nextClassName={"page-item"}
                    nextLinkClassName={"page-link"}
                    breakClassName={"page-item"}
                    breakLinkClassName={"page-link"}
                  />
                )}
              </>
            ) : (
              <p>{t("noPendingRequests")}</p>
            )}
            <div className="text-end">
              {visibleRequests.length > 0 && visibleRequests.length <= requestsPerPage && (
                <a href="#">{t("viewAllRequests")}</a>
              )}
            </div>
          </div>
        </div>

        {showConfirmation && (
          <div className="confirmation-modal-backdrop">
            <div className="confirmation-modal">
              <h2>{actionType === "approve" ? t("approveUsers") : t("denyUsers")}</h2>
              <p
                style={{
                  fontSize: "19px",
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                {t("confirmationPrompt", { count: trulySelectedCount, action: t(actionType) })}
              </p>
              <div className="confirmation-buttons">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleConfirm}
                  style={{ background: "#28A745", border: "none", color: "white" }}
                >
                  {t("confirm")}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleCancelConfirmation}
                  style={{ background: "#DC3545", border: "none", color: "white" }}
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingRequestsPopdown;